import React, { useCallback, useEffect, useState } from "react";
import {
  aiSearchAdminService,
  AiSearchUsageRow,
} from "../../services/admin/aiSearch.admin.service";
import { DataTable, DataTablePagination } from "@/components/ui/data-table";
import type { ColumnDef } from "@tanstack/react-table";
import { Coins, Plus, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "react-hot-toast";

function currentMonthKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function AiSearchCredits() {
  const [rows, setRows] = useState<AiSearchUsageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [monthKey, setMonthKey] = useState(currentMonthKey());
  const [extendTarget, setExtendTarget] = useState<AiSearchUsageRow | null>(
    null
  );
  const [extraCredits, setExtraCredits] = useState(25);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await aiSearchAdminService.getUsage({
        page,
        limit: pageSize,
        monthKey,
      });
      setRows(res.data);
      setTotalPages(res.pagination.totalPages || 1);
      setTotal(res.pagination.total);
    } catch {
      toast.error("Failed to load Social search usage");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, monthKey]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleExtend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!extendTarget) return;
    if (extraCredits < 1) {
      toast.error("Enter at least 1 credit");
      return;
    }

    setSubmitting(true);
    try {
      await aiSearchAdminService.extendCredits({
        brandId: extendTarget.brandId,
        extraCredits,
        note: note.trim() || undefined,
      });
      toast.success(`Added ${extraCredits} credits for ${extendTarget.brandName}`);
      setExtendTarget(null);
      setNote("");
      setExtraCredits(25);
      await load();
    } catch {
      toast.error("Failed to extend credits");
    } finally {
      setSubmitting(false);
    }
  };

  const columns: ColumnDef<AiSearchUsageRow>[] = [
    {
      id: "brandName",
      header: "Brand",
      meta: { width: "w-44" },
      cell: ({ row }) => (
        <div className="font-medium text-foreground truncate">
          {row.original.brandName}
        </div>
      ),
    },
    {
      id: "email",
      header: "Email",
      meta: { width: "w-52" },
      cell: ({ row }) => (
        <div className="text-xs text-muted-foreground truncate">
          {row.original.email}
        </div>
      ),
    },
    {
      id: "planName",
      header: "Plan",
      meta: { width: "w-36" },
      cell: ({ row }) => (
        <span className="text-muted-foreground whitespace-nowrap">
          {row.original.planName} · {row.original.base}/mo
        </span>
      ),
    },
    {
      id: "used",
      header: "Used",
      meta: { width: "w-20" },
      cell: ({ row }) => (
        <span className="font-medium text-foreground">{row.original.used}</span>
      ),
    },
    {
      id: "extended",
      header: "Extended",
      meta: { width: "w-24" },
      cell: ({ row }) => (
        <span className="text-muted-foreground">{row.original.extended}</span>
      ),
    },
    {
      id: "purchased",
      header: "Pack credits",
      meta: { width: "w-28" },
      cell: ({ row }) => (
        <span className="text-muted-foreground">{row.original.purchased}</span>
      ),
    },
    {
      id: "remaining",
      header: "Remaining",
      meta: { width: "w-28" },
      cell: ({ row: { original: row } }) => (
        <span
          className={`font-semibold ${
            row.remaining <= 2 ? "text-red-600 dark:text-red-400" : "text-emerald-700 dark:text-emerald-300"
          }`}
        >
          {row.remaining} / {row.base + row.extended + row.purchased}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      meta: { width: "w-28" },
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => setExtendTarget(row.original)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#d7785b] border border-[#d7785b]/40 rounded-lg hover:bg-[#d7785b]/10 transition-colors"
        >
          <Plus size={14} />
          Extend
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl border border-border flex items-center justify-center">
            <Sparkles className="text-[#d7785b]" size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Social Search Credits
            </h1>
            <p className="text-sm text-muted-foreground">
              Track monthly search usage and extend brand allowances.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="month"
            value={monthKey}
            onChange={(e) => {
              setPage(1);
              setMonthKey(e.target.value);
            }}
            className="rounded-lg border border-border px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-border rounded-lg hover:bg-accent"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide mb-1">
            <Coins size={14} />
            Brands listed
          </div>
          <div className="text-2xl font-semibold text-foreground">{total}</div>
        </div>
        <div className="border border-border rounded-xl p-4">
          <div className="text-muted-foreground text-xs uppercase tracking-wide mb-1">
            Month
          </div>
          <div className="text-2xl font-semibold text-foreground">{monthKey}</div>
        </div>
        <div className="border border-border rounded-xl p-4">
          <div className="text-muted-foreground text-xs uppercase tracking-wide mb-1">
            Base allowance
          </div>
          <div className="text-2xl font-semibold text-foreground">By plan</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Starter 3 · Growth/Professional 5 · Scale/Premium 10 · Packs $50/50
          </div>
        </div>
      </div>

      <div className="bg-card rounded-xl border border-border shadow-sm">
        <DataTable columns={columns} data={rows} isLoading={loading} />

        <DataTablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          onPageChange={setPage}
          noun="brands"
        />
      </div>

      {extendTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <form
            onSubmit={handleExtend}
            className="w-full max-w-md bg-card rounded-xl border border-border shadow-xl p-5 space-y-4"
          >
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                Extend credits
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Add extra Social search credits for{" "}
                <span className="font-medium text-foreground">
                  {extendTarget.brandName}
                </span>{" "}
                this month ({monthKey}).
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Extra credits
              </label>
              <input
                type="number"
                min={1}
                max={10000}
                value={extraCredits}
                onChange={(e) => setExtraCredits(Number(e.target.value))}
                className="w-full rounded-lg border border-border px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Note (optional)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-border px-3 py-2 text-sm"
                placeholder="Reason for extension…"
              />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setExtendTarget(null)}
                className="px-3 py-2 text-sm rounded-lg border border-border hover:bg-accent"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-3 py-2 text-sm rounded-lg bg-[#d7785b] text-white hover:bg-[#c6684c] disabled:opacity-50"
              >
                {submitting ? "Saving…" : "Extend credits"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
